/**
 * Full-Page Demo Mode
 *
 * When the widget receives iframe commands from the agent, it transitions
 * to this full-page layout: an iframe showing the product on the left,
 * and a chat/voice panel on the right. The agent narrates and controls
 * the product via postMessage commands to an IframeAgentMode running
 * inside the iframe.
 */

import { IRAA_LOGO_DATA_URI } from '../brand.js';

// ─── Browser Speech API Types ─────────────────────────────────────────────

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: Event) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

// ─── Types ────────────────────────────────────────────────────────────────

interface DAMessage {
  __da: true;
  id: string;
  gen: number;
  ts: number;
  dir: 'parent-to-iframe' | 'iframe-to-parent';
  type: string;
  payload: Record<string, unknown>;
}

/**
 * Mirror of the iframe-side ScreenObservation (see element-mapper.ts).
 * Duplicated here as a structural type so the agent bundle stays small —
 * full-page-demo is shipped to visitors and ought not import iframe-only
 * modules at build time.
 */
interface ScreenObservation {
  url: string;
  title: string;
  h1?: string;
  alerts: Array<{ role: string; text: string; severity: 'error' | 'warn' | 'info' }>;
  validationErrors: Array<{ near: string; text: string }>;
  modal?: { title: string; bodyText: string };
  unexpectedTextHits: string[];
  forms?: Array<{
    id: string;
    fields: Array<{
      name: string;
      type: string;
      label: string;
      selector?: string;
      validation?: { required?: boolean; min?: string; max?: string; minlength?: number; maxlength?: number; pattern?: string };
      options?: Array<{ value: string; label: string }>;
      checked?: boolean;
      groupName?: string;
    }>;
  }>;
}

/**
 * Mirror of the iframe-side VerificationResult (see automation/actions.ts).
 * Same reasoning as ScreenObservation — kept as a structural type so the
 * widget bundle doesn't have to pull in the action helpers' transitive deps.
 */
interface ActionVerification {
  status: 'verified' | 'executed_unverified' | 'failed';
  observed: {
    domMutated?: boolean;
    routeChanged?: boolean;
    activeElementChanged?: boolean;
    valueAfter?: string;
    valueExpected?: string;
  };
  errors?: Array<{
    code: 'value_mismatch' | 'no_observable_effect' | 'animation_in_progress' | 'timeout';
    message: string;
  }>;
  latencyMs: number;
  resolverStrategy: 'direct' | 'aria' | 'text' | 'tail';
}

export interface DemoAccountInfo {
  loginUrl: string;
  loginMethod: 'form' | 'api' | 'sso';
  fields: Record<string, string>;
  submitSelector?: string;
}

export interface FullPageDemoConfig {
  apiUrl: string;
  apiKey: string;
  sessionId: string;
  sessionToken: string;
  productUrl: string;
  /**
   * Transport callbacks. The mode is transport-agnostic — the host wires these
   * to whatever underlying channel makes sense (WebSocket, HTTPS POST, etc.).
   * `sendVisitorMessage` should arrange for an `agent_response` to be
   * delivered back via `handleWSMessage`.
   *
   * `opts.isAutoContinue` is set when the widget itself fires a synthetic
   * continuation tick (after a turn whose nextTurnHint was 'auto_continue').
   * The host should forward it as `isAutoContinue: true` in the chat
   * request body so the server suppresses message persistence + the
   * visitor-turn cap and routes the LLM call as an internal directive.
   */
  sendVisitorMessage: (text: string, opts?: { isAutoContinue?: boolean }) => void;
  sendLeadCapture: (data: Record<string, string>) => void;
  existingMessages: Array<{ id: string; role: 'visitor' | 'agent'; content: string; ts: number }>;
  agentName: string;
  /**
   * Spoken form of the opening greeting, played once after onboarding clears
   * autoplay. Without this the templated greeting renders as text only —
   * visitors hear silence on session-start and reasonably conclude the demo
   * never greeted them.
   */
  initialGreetingSpeech?: string;
  /**
   * Server-issued message id for the greeting row. Threaded into the TTS
   * request so the dashboard replay can play the *original* greeting MP3
   * at the moment it actually played.
   */
  initialGreetingMessageId?: string;
  /**
   * If supplied, the iframe lands at the demo account's login URL and the
   * agent auto-fills credentials before the showcase begins. Public guide-mode
   * demos only — these accounts are sandboxed and visible to anyone with the
   * demo URL by design.
   */
  demoAccount?: DemoAccountInfo;
}

// Hide TTS + STT from end users while voice work is paused.
// Flip back to true to restore the speaker / mic UI.
const VOICE_ENABLED = true;

function isScreenObservation(v: unknown): v is ScreenObservation {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.url === 'string' &&
    typeof o.title === 'string' &&
    Array.isArray(o.alerts) &&
    Array.isArray(o.validationErrors) &&
    Array.isArray(o.unexpectedTextHits)
  );
}

function isActionVerification(v: unknown): v is ActionVerification {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    (o.status === 'verified' || o.status === 'executed_unverified' || o.status === 'failed') &&
    typeof o.observed === 'object' &&
    typeof o.latencyMs === 'number'
  );
}

// ─── Main Class ───────────────────────────────────────────────────────────

export class FullPageDemoMode {
  private config: FullPageDemoConfig;

  // DOM refs
  private container: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private typingEl: HTMLElement | null = null;
  private micBtnEl: HTMLElement | null = null;
  private speakerBtnEl: HTMLElement | null = null;
  private tourEl: HTMLElement | null = null;
  private tourListEl: HTMLElement | null = null;
  private tourStops: Array<{ url: string; label: string }> = [];

  // Iframe communication
  private commandIdCounter = 0;
  private currentGen = 0;
  private pendingCommands = new Map<string, {
    resolve: (data: unknown) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();
  private iframeReady = false;
  private readyResolve: (() => void) | null = null;

  // Voice
  private ttsEnabled = false;
  private voiceEnabled = false;
  private recognition: SpeechRecognition | null = null;
  private isListening = false;
  private micToggled = false;
  private currentAudio: HTMLAudioElement | null = null;
  private isSpeaking = false;
  // Set the moment speakResponse starts work (before the TTS fetch) and
  // cleared only when playback ends/errors. currentAudio alone leaves a race
  // window during fetch+blob-decode where a second call would fire a parallel
  // request and play it on top of the first.
  private ttsBusy = false;
  private micPrimed = false;
  // Tracks when the browser blocked TTS playback because no user gesture
  // had been recorded yet. Surfaced via getVoiceState() so the server-side
  // agent can write fuller captions instead of narrating into silence.
  private autoplayBlocked = false;

  // Speech queue: agent responses can arrive faster than audio plays (e.g.
  // greeting still in flight when the first LLM reply lands, or visitor
  // sending two messages back-to-back). Serialize playback so voices never
  // overlap. Uncapped — every queued clip plays so the live audio matches
  // the transcript and the dashboard replay stays aligned with both.
  private speechQueue: Array<{ text: string; messageId?: string }> = [];

  // Command queue. Each command optionally carries a `pacing` annotation
  // ('speak_first' default | 'parallel' | 'act_first') sourced from the PDP
  // flow step. The unified pacing engine in processCommandQueue uses this to
  // sequence iframe actions against TTS playback so narration and visual
  // movement match a human SE's cadence rather than racing each other.
  private commandQueue: Array<{
    type: string;
    params: Record<string, unknown>;
    pacing?: 'speak_first' | 'parallel' | 'act_first';
  }> = [];
  private processingCommands = false;

  // Pacing constants — see Cluster A items 1–5 in the senior-engineer review.
  // INTENT_ACK_MS is the human-cadence beat between narration finishing and
  // the action firing ("I'll click Save…" pause "click"). Anything under
  // 200ms reads as a race; anything over 500ms reads as a stall.
  private static readonly INTENT_ACK_MS = 350;
  // Post-action settle: after the iframe reports a command result, wait this
  // long before picking up the next command so the visitor's eyes can land
  // on the changed UI before the next thing moves.
  private static readonly POST_ACTION_SETTLE_MS = 320;
  // Speech-idle poll: how often processCommandQueue rechecks isSpeaking when
  // pacing is 'speak_first' and TTS is still playing.
  private static readonly SPEECH_IDLE_POLL_MS = 80;

  // Recent iframe command results (last N). Drained by the host on each
  // visitor turn and forwarded to the server so the agent can recover from
  // selector misses ("hmm, the button isn't there — let me try…") instead
  // of narrating into a void. `recoveredSelector` is non-null when the
  // iframe's self-healing resolver had to fall back from the primary
  // selector — the platform side persists this so PDP authors get a
  // 1-click "apply suggestion" in the dashboard (#42).
  private recentCommandResults: Array<{
    type: string;
    ok: boolean;
    error?: string;
    ts: number;
    recoveredSelector?: string;
    /**
     * Post-action screen state — visible alerts, validation errors, modal
     * text, URL/title. Captured inline by IframeAgentMode after each
     * state-changing command, plus parent-driven captures after da:nav-change
     * so the agent sees post-navigation state too. Empty observations are
     * suppressed at the prompt layer to keep token cost flat.
     */
    observation?: ScreenObservation;
    /**
     * Per-action verification (PR1). Tells the agent whether the action took
     * effect (DOM mutated, value matched, route changed) vs. dispatched
     * cleanly but without observable signal. Empty when an action helper
     * doesn't produce one (lead-form, customer-card, book-meeting fall-throughs).
     */
    verification?: ActionVerification;
  }> = [];
  private static readonly MAX_RECENT_RESULTS = 6;

  // Live URL of whatever the iframe is currently displaying. Updated on
  // `da:ready` (initial load + post-navigation re-init) and `da:nav-change`
  // (SPA navigations the iframe-side observer detects). Sent to the server
  // on every chat turn so the agent can reason about page-state truthfully.
  private currentIframeUrl: string | null = null;

  // Cluster E item 44 — heartbeat tracking. The iframe sends `da:pong` every
  // 5s; if we miss 3 in a row (15s) we treat the iframe as frozen and try a
  // single reload. Two consecutive frozen episodes surface a user-facing
  // error so we don't loop reloading a fundamentally broken page.
  private lastHeartbeatTs = 0;
  private heartbeatWatchdog: ReturnType<typeof setInterval> | null = null;
  private heartbeatRecoveryAttempts = 0;
  private static readonly HEARTBEAT_TIMEOUT_MS = 15000;
  private static readonly MAX_HEARTBEAT_RECOVERIES = 2;

  // Outcome of the last auto-login attempt. The agent sees this in its
  // LIVE IFRAME STATE block and uses it to decide whether to retry login,
  // proceed with the showcase, or surface a failure to the visitor.
  private loginState: 'unknown' | 'pending' | 'logged_in' | 'failed' = 'unknown';

  // Silence-nudge: only arms when the agent ended a turn with a question and
  // the visitor hasn't replied. Statements/narration never nudge — silence is
  // a normal beat, not a bug. Capped at one nudge per session; after that, the
  // server-side disengagement-detector handles re-engagement based on reply
  // patterns. Action turns (click/navigate/login) get a longer wait because
  // the visitor is reading the page that just changed.
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private silenceNudgeCount = 0;
  private lastTurnEndedWithQuestion = false;
  private lastTurnWasAction = false;
  private static readonly MAX_SILENCE_NUDGES = 1;
  private static readonly SILENCE_NUDGE_MS_NORMAL = 45000;
  private static readonly SILENCE_NUDGE_MS_ACTION = 90000;
  private static readonly SILENCE_NUDGE_VARIANTS = [
    "Still with you — let me know what you're thinking.",
    "No rush, just checking in whenever you're ready.",
    "Take your time — happy to keep going when you are.",
  ];

  // Auto-continue: the server-side agent emits `nextTurnHint` on every turn.
  // When it's 'auto_continue', the widget fires a synthetic continuation
  // tick after iframe commands settle and the spoken narration finishes,
  // so a visitor who said "show me the dashboard" once gets a fully-paced
  // multi-step demo without needing to type "ok" between every action.
  // The budget caps how many ticks fire before we yield to the visitor —
  // resets to AUTO_CONTINUE_BUDGET on any genuine visitor input. The grace
  // window gives the visitor a chance to interrupt before the next tick.
  private static readonly AUTO_CONTINUE_BUDGET = 6;
  private static readonly AUTO_CONTINUE_GRACE_MS = 800;
  private static readonly AUTO_CONTINUE_POLL_MS = 200;
  private autoContinueTimer: ReturnType<typeof setTimeout> | null = null;
  private autoContinueRemaining = FullPageDemoMode.AUTO_CONTINUE_BUDGET;

  // Callbacks
  onExit: (() => void) | null = null;

  constructor(config: FullPageDemoConfig) {
    this.config = config;
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  async start(): Promise<void> {
    this.renderLayout();
    this.setupIframeListener();
    this.loadProductInIframe();
    this.initVoice();

    // Replay existing messages
    for (const msg of this.config.existingMessages) {
      if (msg.role === 'agent') {
        this.addAgentMessage(msg.content);
      } else {
        this.addUserMessage(msg.content);
      }
    }

    // Speak the opening greeting. Onboarding (a click) just cleared the
    // browser's autoplay gate, so TTS audio plays without prompting. If TTS
    // is disabled or fails, speakResponse arms the silence timer on its own;
    // if it succeeds, the timer arms on `audio.onended`.
    if (this.config.initialGreetingSpeech) {
      const greeting = this.config.initialGreetingSpeech;
      this.lastTurnEndedWithQuestion = /\?\s*$/.test(greeting.trim());
      this.lastTurnWasAction = false;
      this.speakResponse(greeting, this.config.initialGreetingMessageId);
    }

    // Wait for iframe to be ready
    try {
      await this.waitForIframeReady(15000);
    } catch {
      this.addSystemMessage('Product is loading — some features may take a moment to respond.');
    }

    // Auto-login to the demo account before the agent starts driving
    if (this.config.demoAccount) {
      await this.autoLoginToDemoAccount();
    }

    // Process any queued commands
    this.processCommandQueue();

    // Cluster E item 44 — start the heartbeat watchdog after first ready.
    this.startHeartbeatWatchdog();
  }

  private startHeartbeatWatchdog(): void {
    this.lastHeartbeatTs = Date.now();
    this.heartbeatWatchdog = setInterval(() => {
      const since = Date.now() - this.lastHeartbeatTs;
      if (since < FullPageDemoMode.HEARTBEAT_TIMEOUT_MS) return;
      // Iframe has gone silent. Try one reload; if it happens again, surface
      // a frame-blocked message instead of looping.
      if (this.heartbeatRecoveryAttempts >= FullPageDemoMode.MAX_HEARTBEAT_RECOVERIES) {
        this.addSystemMessage(
          'The product page has stopped responding. This sometimes happens when the site blocks embedding — try reopening the demo link in a new tab.',
        );
        if (this.heartbeatWatchdog) {
          clearInterval(this.heartbeatWatchdog);
          this.heartbeatWatchdog = null;
        }
        return;
      }
      this.heartbeatRecoveryAttempts++;
      this.addSystemMessage('Reconnecting to the product…');
      this.iframeReady = false;
      // Reload the iframe — the agent's child boots fresh and re-establishes
      // postMessage. Reset lastHeartbeatTs so we don't immediately re-trip.
      if (this.iframe) {
        const url = this.iframe.src;
        this.iframe.src = 'about:blank';
        // Force browser to actually navigate before the next assignment.
        setTimeout(() => {
          if (this.iframe) this.iframe.src = url;
          this.lastHeartbeatTs = Date.now();
        }, 80);
      }
    }, 5000);
  }

  stop(): void {
    window.removeEventListener('message', this.handleIframeMessage);
    for (const pending of this.pendingCommands.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error('Stopped'));
    }
    this.pendingCommands.clear();
    this.clearSilenceTimer();
    this.clearAutoContinueTimer();
    this.clearSpeechQueue();
    if (this.heartbeatWatchdog) {
      clearInterval(this.heartbeatWatchdog);
      this.heartbeatWatchdog = null;
    }
    this.stopVoice();
    this.container?.remove();
    this.container = null;
    this.shadow = null;
    this.iframe = null;
  }

  // ─── Public API (called by widget) ─────────────────────────────────────

  queueCommands(
    commands: Array<{
      type: string;
      params: Record<string, unknown>;
      pacing?: 'speak_first' | 'parallel' | 'act_first';
    }>,
  ): void {
    this.commandQueue.push(...commands);
    this.processCommandQueue();
  }

  /**
   * Block until the speech queue and active TTS playback are both idle.
   * Used by the unified pacing engine to gate iframe actions on the matching
   * narration completing. Bounded by `timeoutMs` so a stuck TTS never freezes
   * the demo — if we time out we proceed anyway and let the visual run.
   */
  private async waitForSpeechIdle(timeoutMs = 8000): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while ((this.isSpeaking || this.speechQueue.length > 0) && Date.now() < deadline) {
      await this.delay(FullPageDemoMode.SPEECH_IDLE_POLL_MS);
    }
  }

  handleWSMessage(msg: { type: string; payload: Record<string, unknown> }): void {
    switch (msg.type) {
      case 'agent_response': {
        this.typingEl?.classList.add('da-fpd-hidden');
        const text = msg.payload.text as string;
        const speech = (msg.payload.speech as string) || text;
        const commands = msg.payload.iframeCommands as Array<{ type: string; params: Record<string, unknown> }> | undefined;
        const nextTurnHint = msg.payload.nextTurnHint as 'auto_continue' | 'wait_for_visitor' | undefined;
        const messageId = typeof msg.payload.messageId === 'string' ? msg.payload.messageId : undefined;

        // Set turn context BEFORE speakResponse so the silence timer it arms
        // (synchronously in the text-only path, async in audio.onended) reads
        // the right flags.
        this.lastTurnEndedWithQuestion = /\?\s*$/.test(text?.trim() ?? '');
        this.lastTurnWasAction = !!commands?.length;

        this.addAgentMessage(text);

        // Speak the response
        this.speakResponse(speech, messageId);

        // Queue iframe commands
        if (commands?.length) {
          this.queueCommands(commands);
        }

        // If the server says "auto_continue" and we still have budget, fire
        // a synthetic continuation tick once iframe commands + TTS settle.
        // See scheduleAutoContinue for the wait-for-quiet logic.
        this.scheduleAutoContinue(nextTurnHint);
        break;
      }
      case 'typing_indicator': {
        if (msg.payload.isTyping) {
          this.typingEl?.classList.remove('da-fpd-hidden');
        } else {
          this.typingEl?.classList.add('da-fpd-hidden');
        }
        break;
      }
      case 'lead_form': {
        // Show lead form inline
        const fields = msg.payload.fields as string[] | undefined;
        this.showLeadForm(fields ?? ['email', 'company']);
        break;
      }
    }
  }

  // ─── Command Processing ─────────────────────────────────────────────────

  private async processCommandQueue(): Promise<void> {
    if (this.processingCommands) return;
    this.processingCommands = true;

    while (this.commandQueue.length > 0) {
      const cmd = this.commandQueue.shift()!;
      // Unified pacing: wait for the matching narration to finish before
      // dispatching the action, then take a beat (INTENT_ACK_MS) so the
      // click doesn't fire on top of the last syllable. Skip the wait when
      // PDP authored 'parallel' or 'act_first' for the step.
      const pacing = cmd.pacing ?? 'speak_first';
      if (pacing === 'speak_first') {
        await this.waitForSpeechIdle();
        await this.delay(FullPageDemoMode.INTENT_ACK_MS);
      } else if (pacing === 'act_first') {
        // act_first: dispatch immediately, but still queue the next command
        // behind any speech that lands while this one runs.
      }
      try {
        if (cmd.type === 'da:navigate') {
          this.recordTourStop(cmd.params.url as string | undefined);
        }
        // Lead form is a chat-panel side effect, not an iframe command —
        // intercept here rather than postMessaging an unsupported command.
        if (cmd.type === 'da:lead-form') {
          const fields = (cmd.params.fields as string[] | undefined) ?? ['email', 'company'];
          this.showLeadForm(fields);
          this.recordCommandResult(cmd.type, true);
          await this.delay(150);
          continue;
        }
        // Customer success card — chat-panel overlay, no iframe message.
        // Triggered by the cite_customer_story tool. Card data comes
        // pre-resolved server-side from pdp.knowledge.customer_stories.
        if (cmd.type === 'da:customer-card') {
          this.showCustomerCard(cmd.params);
          this.recordCommandResult(cmd.type, true);
          await this.delay(150);
          continue;
        }
        // Booking widget — chat-panel CTA, opens the configured URL in a
        // new tab. Triggered by the book_meeting tool when buying signals
        // stack. Same chat-panel-side-effect shape as lead-form.
        if (cmd.type === 'da:book-meeting') {
          this.showBookMeeting(cmd.params);
          this.recordCommandResult(cmd.type, true);
          await this.delay(150);
          continue;
        }
        const result = await this.sendIframeCommand(cmd.type, cmd.params);
        // The iframe-side action helpers return `{ recoveredSelector }`
        // when self-healing kicks in. Surface that to the platform so it
        // can power PDP auto-correct (#42); on the agent prompt side it
        // also tells the model that the click landed even though the
        // primary selector missed.
        const recoveredSelector =
          result && typeof result === 'object' && 'recoveredSelector' in result
            ? (result as { recoveredSelector?: unknown }).recoveredSelector
            : undefined;
        // Post-action screen observation (alerts, validation errors, etc.)
        // — IframeAgentMode runs extractScreenObservation after every
        // state-changing command and packs it into the result envelope.
        const observation =
          result && typeof result === 'object' && 'observation' in result
            ? (result as { observation?: unknown }).observation
            : undefined;
        // Per-action verification (PR1) — did the click actually move
        // anything, did the typed value stick, did the option select.
        const verification =
          result && typeof result === 'object' && 'verification' in result
            ? (result as { verification?: unknown }).verification
            : undefined;
        this.recordCommandResult(
          cmd.type,
          true,
          undefined,
          typeof recoveredSelector === 'string' ? recoveredSelector : undefined,
          isScreenObservation(observation) ? observation : undefined,
          isActionVerification(verification) ? verification : undefined,
        );
        // Post-action settle: gives the visitor's eyes a beat to track the
        // changed UI before the next command runs. The named constant keeps
        // pacing tunable from one place.
        await this.delay(FullPageDemoMode.POST_ACTION_SETTLE_MS);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.recordCommandResult(cmd.type, false, message);
        console.warn('[FullPageDemo] Command failed:', cmd.type, err);
      }
    }

    this.processingCommands = false;
  }

  private recordCommandResult(
    type: string,
    ok: boolean,
    error?: string,
    recoveredSelector?: string,
    observation?: ScreenObservation,
    verification?: ActionVerification,
  ): void {
    this.recentCommandResults.push({
      type,
      ok,
      error,
      ts: Date.now(),
      recoveredSelector,
      observation,
      verification,
    });
    if (this.recentCommandResults.length > FullPageDemoMode.MAX_RECENT_RESULTS) {
      this.recentCommandResults.shift();
    }
  }

  /**
   * Drain the recent command results buffer. Called by the host before each
   * visitor turn so the server-side agent can see what actually happened in
   * the iframe (vs. what it told the iframe to do).
   */
  drainRecentCommandResults(): Array<{
    type: string;
    ok: boolean;
    error?: string;
    ts: number;
    recoveredSelector?: string;
    observation?: ScreenObservation;
    verification?: ActionVerification;
  }> {
    const drained = this.recentCommandResults;
    this.recentCommandResults = [];
    return drained;
  }

  // ─── Iframe Communication ──────────────────────────────────────────────

  private setupIframeListener(): void {
    window.addEventListener('message', this.handleIframeMessage);
  }

  private handleIframeMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    // Only accept messages from our own iframe. This protects the parent
    // page from a malicious popup or sibling window posting forged
    // `iframe-to-parent` results that could short-circuit the verifier
    // or leak the session token via the boot-request reply.
    const expectedSource = this.iframe?.contentWindow ?? null;
    if (expectedSource && event.source !== expectedSource) return;

    // Iframe-side boot fallback: when window.name didn't reach the inner
    // page (sandbox/credentialless quirk, or cross-origin redirect that
    // reset the browsing-context name), the child posts this and we reply
    // with the token it would have read from window.name. See
    // requestIframeTokenFromParent in src/index.ts.
    if ((data as { __iraa?: string }).__iraa === 'iframe-boot-request') {
      this.iframe?.contentWindow?.postMessage(
        { __iraa: 'iframe-init', kind: 'da_demo_iframe', token: this.config.sessionId },
        '*',
      );
      return;
    }

    if (!data.__da || data.dir !== 'iframe-to-parent') return;

    const msg = data as DAMessage;

    if (msg.type === 'da:ready') {
      this.iframeReady = true;
      this.readyResolve?.();
      // Capture the URL the iframe just landed on. Fires both on initial
      // load and after each SPA navigation (the inner iframe re-emits it).
      const url = msg.payload.url as string | undefined;
      if (url) this.currentIframeUrl = url;
      // Hand session credentials to the iframe child so it can POST rrweb
      // batches up to the platform. We post on every da:ready (the iframe
      // re-emits it after each SPA navigation inside the customer site);
      // IframeAgentMode dedupes — a recorder is started at most once.
      this.postSessionCredentialsToIframe();
      return;
    }

    if (msg.type === 'da:cmd-result') {
      const pending = this.pendingCommands.get(msg.payload.id as string);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingCommands.delete(msg.payload.id as string);
        if (msg.payload.ok) {
          pending.resolve(msg.payload.data);
        } else {
          pending.reject(new Error(msg.payload.error as string || 'Command failed'));
        }
      }
      return;
    }

    if (msg.type === 'da:pong') {
      // Cluster E item 44 — track heartbeat liveness. Watchdog (started in
      // start()) reads lastHeartbeatTs and decides when to recover.
      this.lastHeartbeatTs = Date.now();
      return;
    }

    if (msg.type === 'da:nav-change') {
      const url = msg.payload.url as string | undefined;
      if (url) this.currentIframeUrl = url;
      // Capture post-nav screen state — SPA route changes don't go through
      // processCommandQueue's inline observation, so explicitly ask the iframe
      // for one now. Coalesced via captureNavObservation() so a burst of
      // nav-change events (some apps fire several during a single transition)
      // produces at most one capture.
      void this.captureNavObservation();
    }
  };

  // Coalesces back-to-back da:nav-change events (some routers emit several
  // during a single transition) into a single capture per ~350ms window.
  private navObservationTimer: ReturnType<typeof setTimeout> | null = null;

  private captureNavObservation(): void {
    if (this.navObservationTimer) return;
    this.navObservationTimer = setTimeout(async () => {
      this.navObservationTimer = null;
      try {
        // Wait for the SPA's first paint to settle before scanning. Most
        // routers update the DOM synchronously after pushState; an extra
        // 200ms covers the delay before hydration / data-fetch placeholders
        // resolve into actual content (or error states).
        const result = await this.sendIframeCommand('da:capture-observation', {});
        const observation =
          result && typeof result === 'object' && 'observation' in result
            ? (result as { observation?: unknown }).observation
            : undefined;
        if (isScreenObservation(observation)) {
          this.recordCommandResult('da:nav-change', true, undefined, undefined, observation);
        }
      } catch {
        // Best-effort — never let observation capture break the demo.
      }
    }, 350);
  }

  /**
   * Latest URL the iframe is displaying, captured from da:ready/da:nav-change
   * events. Read by the host before each chat POST so the server-side agent
   * can reason about where the visitor actually is.
   */
  getCurrentIframeUrl(): string | null {
    return this.currentIframeUrl;
  }

  /**
   * Outcome of the last login attempt — sent to the server every chat turn so
   * the agent's LIVE IFRAME STATE block reflects whether to retry, proceed,
   * or narrate a failure.
   */
  getLoginState(): 'unknown' | 'pending' | 'logged_in' | 'failed' {
    return this.loginState;
  }

  /**
   * Voice state for the LLM. Shipped to the server every chat turn so the
   * agent can adapt narration: 'on' = speak naturally, 'off' = visitor will
   * read text only (write longer, more explanatory captions), 'autoplay_blocked'
   * = audio is gated behind a click and visitor sees text right now. This
   * fixes the previous behaviour where TTS 501 silently muted the agent
   * without telling the LLM to compensate (Cluster A item 9).
   */
  getVoiceState(): 'on' | 'off' | 'autoplay_blocked' {
    if (!VOICE_ENABLED) return 'off';
    if (!this.ttsEnabled) return 'off';
    if (this.autoplayBlocked) return 'autoplay_blocked';
    return 'on';
  }

  /**
   * Block until auto-login resolves to a terminal state ('logged_in' or
   * 'failed') or `timeoutMs` elapses. Used by the chat layer to avoid
   * sending the visitor's first message while loginState is still 'pending'
   * — without this gate the server-side agent sees stale state and ends up
   * narrating "let me get us logged in" while the iframe is mid-redirect to
   * the dashboard.
   */
  async waitForLoginSettled(timeoutMs: number): Promise<'unknown' | 'pending' | 'logged_in' | 'failed'> {
    const deadline = Date.now() + timeoutMs;
    while (this.loginState === 'pending' && Date.now() < deadline) {
      await this.delay(100);
    }
    return this.loginState;
  }

  private loadProductInIframe(): void {
    if (!this.iframe) return;
    // Land on the demo account's login URL when one is configured so the
    // showcase happens against authenticated demo state, not the public site.
    const baseUrl = this.config.demoAccount?.loginUrl ?? this.config.productUrl;
    // Pass the iframe-mode token via iframe.name (not a query param). SPAs
    // routinely strip query strings on first render via history.replaceState
    // before our async-loaded script reads location.search; iframe.name maps
    // to window.name inside the frame and survives every same-origin
    // navigation the customer's app makes (login redirect, route change,
    // etc.), so the token is durably available to every page load.
    // Set iframe.name BEFORE iframe.src so the value is in place when boot
    // runs on first navigation.
    this.iframe.name = `da_demo_iframe:${this.config.sessionId}`;
    this.iframe.src = baseUrl;
  }

  /**
   * Drive the iframe through the demo-account login flow before handing off
   * to the agent. After clicking submit, watch for the iframe URL to leave
   * the login page — that's the only reliable success signal across customer
   * stacks. The outcome is exposed via {@link getLoginState} and surfaced to
   * the server-side agent every chat turn, so the agent can either proceed
   * (logged_in), wait quietly (pending), or narrate a failure (failed).
   * Auto-login is the widget's job — the agent never retries it itself.
   */
  private async autoLoginToDemoAccount(): Promise<void> {
    const account = this.config.demoAccount;
    if (!account || account.loginMethod !== 'form') return;

    // Defensive: a credentials object with submit_selector but no fields would
    // click submit on an empty form. Treat as "no usable creds" — surface as
    // failed so the agent narrates the no-creds fallback next turn.
    if (!account.fields || Object.keys(account.fields).length === 0) {
      this.loginState = 'failed';
      return;
    }

    this.loginState = 'pending';
    const preSubmitUrl = this.currentIframeUrl;
    const loginPath = pathFromUrl(account.loginUrl);
    const fieldSelectors = Object.keys(account.fields);
    const firstSelector = fieldSelectors[0];

    // Per-step diagnostic. Pushed into the recentCommandResults buffer so
    // the next /chat turn carries it to the server, where the existing
    // audit-event pipeline persists `iframe_command_failed` rows that the
    // dashboard outcome classifier and PDP author can inspect. Without
    // this, autoLogin failures left no breadcrumb anywhere — the entire
    // login attempt would silently flip to `loginState='failed'` with no
    // record of which selector missed or how long the form took to render.
    const recordAutoLoginStep = (
      step: 'wait_for_form' | 'type_field' | 'click_submit' | 'wait_redirect',
      ok: boolean,
      args: { selector?: string; error?: string; durationMs?: number },
    ): void => {
      this.recordCommandResult(
        `auto_login:${step}`,
        ok,
        args.error,
        undefined,
        undefined,
        undefined,
      );
      if (!ok) {
        console.warn(
          `[FullPageDemo] auto-login step ${step} failed`,
          args.selector ?? '',
          args.error ?? '',
        );
      }
    };

    try {
      // React/Vue SPAs render the login form AFTER the iframe `load` event
      // fires (HTML shell loads first, JS hydrates, then form mounts). A
      // fixed delay is unreliable across cold/warm caches — poll for the
      // first credential selector instead. Without this, da:type races the
      // hydration on slower-rendering apps and throws "target not found"
      // before the input exists, silently failing the entire auto-login.
      if (firstSelector) {
        const t0 = Date.now();
        try {
          await this.sendIframeCommand('da:wait-for-element', {
            selector: firstSelector,
            timeout: 8000,
          });
          recordAutoLoginStep('wait_for_form', true, {
            selector: firstSelector,
            durationMs: Date.now() - t0,
          });
        } catch (err) {
          // The form never appeared within the budget. Most likely cause:
          // the configured selector doesn't match the customer's actual
          // login form (PDP drift, or a setup-time config mistake). Record
          // the miss and bail — typing into a non-existent element will
          // only produce a less-informative `da:type` failure downstream.
          const message = err instanceof Error ? err.message : String(err);
          recordAutoLoginStep('wait_for_form', false, {
            selector: firstSelector,
            error: message,
            durationMs: Date.now() - t0,
          });
          this.loginState = 'failed';
          return;
        }
      } else {
        await this.delay(400);
      }

      for (const [selector, value] of Object.entries(account.fields)) {
        const t0 = Date.now();
        try {
          await this.sendIframeCommand('da:type', { selector, text: value, speed: 30 });
          recordAutoLoginStep('type_field', true, {
            selector,
            durationMs: Date.now() - t0,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          recordAutoLoginStep('type_field', false, {
            selector,
            error: message,
            durationMs: Date.now() - t0,
          });
          throw err;
        }
        await this.delay(150);
      }

      if (account.submitSelector) {
        await this.delay(200);
        const t0 = Date.now();
        try {
          await this.sendIframeCommand('da:click', {
            selector: account.submitSelector,
            cursorAnimate: true,
          });
          recordAutoLoginStep('click_submit', true, {
            selector: account.submitSelector,
            durationMs: Date.now() - t0,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          recordAutoLoginStep('click_submit', false, {
            selector: account.submitSelector,
            error: message,
            durationMs: Date.now() - t0,
          });
          throw err;
        }
      }
    } catch (err) {
      console.warn('[FullPageDemo] Demo account auto-login command failed:', err);
      this.loginState = 'failed';
      return;
    }

    // Now the slow part: the customer's app does the actual auth + redirect.
    // Poll currentIframeUrl (updated by da:nav-change) for up to ~6 s. Login
    // succeeded iff the iframe left the login path. Hash-only changes don't
    // count — many SPA login forms toggle ?error=... while staying on /login.
    const t0 = Date.now();
    const navigated = await this.waitForLoginRedirect(preSubmitUrl, loginPath, 6000);
    const finalPath = this.currentIframeUrl ? pathFromUrl(this.currentIframeUrl) : null;
    recordAutoLoginStep('wait_redirect', navigated, {
      error: navigated
        ? undefined
        : `iframe stayed on ${finalPath ?? 'unknown path'} for 6s after submit — credentials may be wrong, or the redirect is gated by a captcha / 2FA / consent screen`,
      durationMs: Date.now() - t0,
    });
    this.loginState = navigated ? 'logged_in' : 'failed';
  }

  private async waitForLoginRedirect(
    preSubmitUrl: string | null,
    loginPath: string | null,
    timeoutMs: number,
  ): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const now = this.currentIframeUrl;
      if (now && now !== preSubmitUrl) {
        const nowPath = pathFromUrl(now);
        // Only treat as success if we're off the login path. A SPA that
        // re-renders /login?error=bad_creds counts as still on login.
        if (nowPath && loginPath && nowPath !== loginPath) {
          return true;
        }
        if (!loginPath) {
          // No reference login path to compare against — any nav counts.
          return true;
        }
      }
      await this.delay(200);
    }
    return false;
  }

  private waitForIframeReady(timeoutMs: number): Promise<void> {
    if (this.iframeReady) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.readyResolve = null;
        reject(new Error('Iframe ready timeout'));
      }, timeoutMs);
      this.readyResolve = () => {
        clearTimeout(timer);
        resolve();
      };
    });
  }

  private postSessionCredentialsToIframe(): void {
    if (!this.iframe?.contentWindow) return;
    try {
      this.iframe.contentWindow.postMessage(
        {
          __iraa: 'session',
          apiUrl: this.config.apiUrl,
          sessionId: this.config.sessionId,
          sessionToken: this.config.sessionToken,
        },
        '*',
      );
    } catch {
      // Cross-origin postMessage rarely throws, but if it does the recorder
      // simply won't start — the demo itself is unaffected.
    }
  }

  private sendIframeCommand(type: string, payload: Record<string, unknown>, timeoutMs = 15000): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.iframe?.contentWindow) {
        reject(new Error('Iframe not available'));
        return;
      }

      const id = `fpd_${++this.commandIdCounter}_${Date.now().toString(36)}`;
      const timer = setTimeout(() => {
        this.pendingCommands.delete(id);
        reject(new Error(`Command ${type} timed out`));
      }, timeoutMs);

      this.pendingCommands.set(id, { resolve, reject, timer });

      const msg: DAMessage = {
        __da: true,
        id,
        gen: this.currentGen,
        ts: Date.now(),
        dir: 'parent-to-iframe',
        type,
        payload,
      };

      this.iframe.contentWindow.postMessage(msg, '*');
    });
  }

  // ─── Voice ──────────────────────────────────────────────────────────────

  private initVoice(): void {
    if (!VOICE_ENABLED) return;
    // TTS is always available (server-side check happens at call time)
    this.ttsEnabled = true;
    if (this.speakerBtnEl) {
      this.speakerBtnEl.style.display = '';
      this.speakerBtnEl.classList.remove('da-fpd-speaker-off');
    }

    // Check browser STT support
    const SpeechRecognitionCtor =
      (window as unknown as Record<string, unknown>).SpeechRecognition ??
      (window as unknown as Record<string, unknown>).webkitSpeechRecognition;

    if (SpeechRecognitionCtor) {
      this.voiceEnabled = true;
      if (this.micBtnEl) this.micBtnEl.style.display = '';

      this.recognition = new (SpeechRecognitionCtor as new () => SpeechRecognition)();
      // Continuous mode: mic stays open across turns once toggled on, so the
      // user can interrupt the agent and hold a hands-free conversation.
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Barge-in: if the agent is speaking and the user starts talking,
        // cut the audio immediately AND drop any queued agent speech — once
        // the visitor interrupts, anything we had backlogged is stale.
        // 2-char interim threshold avoids single-syllable noise triggering
        // false stops; finals always interrupt.
        if (this.isSpeaking && (interimTranscript.trim().length >= 2 || finalTranscript)) {
          this.clearSpeechQueue();
        }
        // Same threshold cancels a pending auto-continue tick — visitor is
        // engaging, don't fire another agent turn over their voice. Final
        // transcripts go through sendVisitorMessage which resets the budget;
        // interim cancels the timer but keeps the budget so a single mumble
        // doesn't permanently stop the chain.
        if (
          this.autoContinueTimer !== null &&
          (interimTranscript.trim().length >= 2 || finalTranscript)
        ) {
          this.clearAutoContinueTimer();
        }

        if (interimTranscript && this.inputEl) {
          this.inputEl.value = interimTranscript;
        }

        if (finalTranscript) {
          if (this.inputEl) this.inputEl.value = '';
          this.sendVisitorMessage(finalTranscript.trim());
        }
      };

      this.recognition.onend = () => {
        // With continuous mode, onend fires on explicit stop or unrecoverable error.
        this.isListening = false;
        this.updateMicButton();
      };

      this.recognition.onerror = () => {
        this.isListening = false;
        this.updateMicButton();
      };
    }
  }

  /**
   * Prime the mic with explicit AEC/noise-suppression constraints so that
   * the agent's TTS coming out of speakers does not bleed back into the
   * SpeechRecognition stream and falsely interrupt itself.
   */
  private async primeMicrophone(): Promise<void> {
    if (this.micPrimed) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      stream.getTracks().forEach((t) => t.stop());
      this.micPrimed = true;
    } catch {
      // Permission denied or no mic — recognition.start() will surface the error.
    }
  }

  private stopVoice(): void {
    if (this.isListening && this.recognition) {
      this.recognition.abort();
      this.isListening = false;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
      this.isSpeaking = false;
      this.ttsBusy = false;
    }
  }

  private async toggleListening(): Promise<void> {
    if (!this.recognition) return;

    if (this.isListening) {
      this.recognition.stop();
      this.isListening = false;
      this.micToggled = false;
    } else {
      // Clicking the mic while the agent is speaking acts as an interrupt.
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
        this.isSpeaking = false;
        this.ttsBusy = false;
      }
      await this.primeMicrophone();
      try {
        this.recognition.start();
        this.isListening = true;
        this.micToggled = true;
      } catch {
        this.isListening = false;
      }
    }
    this.updateMicButton();
  }

  private updateMicButton(): void {
    if (!this.micBtnEl) return;
    this.micBtnEl.classList.toggle('da-fpd-mic-active', this.isListening);
    this.micBtnEl.setAttribute('aria-label', this.isListening ? 'Stop listening' : 'Voice input');
  }

  private async speakResponse(text: string, messageId?: string): Promise<void> {
    if (!text?.trim()) return;
    // Text-only mode: skip TTS but still arm the silence-nudge so the demo
    // doesn't dead-air when the visitor has voice off.
    if (!this.ttsEnabled) {
      this.startSilenceTimer();
      return;
    }

    // Already speaking OR a TTS fetch is in flight — queue this clip and let
    // the onended/onerror path drain it. Checking ttsBusy (set before fetch)
    // instead of currentAudio (set only after blob decode) is what closes the
    // parallel-speech race. The queue used to evict the oldest entry on
    // overflow, but that broke transcript/audio alignment in replay: the
    // dropped message had no audioId, so the dashboard fell back to browser
    // TTS at message.createdAt and ended up reading the wrong text under the
    // visual at that moment. Keep every clip — the agent doesn't produce
    // unbounded text, so worst case is a brief catch-up.
    if (this.ttsBusy) {
      this.speechQueue.push({ text, messageId });
      return;
    }
    this.ttsBusy = true;

    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/session/${this.config.sessionId}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.sessionToken}`,
        },
        body: JSON.stringify(messageId ? { text, messageId } : { text }),
      });

      if (!resp.ok) {
        // TTS not configured on server — disable silently
        if (resp.status === 501) this.ttsEnabled = false;
        this.ttsBusy = false;
        this.startSilenceTimer();
        this.drainSpeechQueue();
        return;
      }

      // The server returns the audio's storage handle so we can correlate
      // playback timing back to the persisted clip on /audio-played. The
      // header is optional (older API versions omit it) — when missing we
      // fall back to playing without recording timing.
      const audioId = resp.headers.get('X-Audio-Id') ?? resp.headers.get('x-audio-id');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      this.currentAudio = audio;
      this.isSpeaking = true;

      let timingReported = false;
      const reportTiming = () => {
        if (timingReported) return;
        timingReported = true;
        if (!messageId || !audioId) return;
        const startedAtMs = Date.now();
        const durationMs = Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0;
        // Fire-and-forget: dashboard replay accuracy is nice-to-have, not
        // load-bearing on the live experience.
        fetch(`${this.config.apiUrl}/api/v1/session/${this.config.sessionId}/audio-played`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.config.sessionToken}`,
          },
          body: JSON.stringify({ messageId, audioId, startedAtMs, durationMs }),
          keepalive: true,
        }).catch(() => undefined);
      };
      audio.onplaying = () => {
        // First successful playback after onboarding click — the autoplay
        // gate is open. Clear the flag so getVoiceState() reports 'on' from
        // here on (until the next 501 / failure).
        this.autoplayBlocked = false;
        reportTiming();
      };

      audio.onended = () => {
        URL.revokeObjectURL(url);
        this.isSpeaking = false;
        this.currentAudio = null;
        this.ttsBusy = false;
        // Mic stays continuously listening (recognition.continuous = true)
        // when micToggled, so no restart needed here.
        this.startSilenceTimer();
        this.drainSpeechQueue();
      };

      audio.onerror = () => {
        URL.revokeObjectURL(url);
        this.isSpeaking = false;
        this.currentAudio = null;
        this.ttsBusy = false;
        this.startSilenceTimer();
        this.drainSpeechQueue();
      };

      await audio.play().catch(() => {
        // Autoplay blocked — degrade to text-only and tell the LLM via
        // voiceState on the next chat turn so it writes fuller captions.
        this.autoplayBlocked = true;
        this.isSpeaking = false;
        this.currentAudio = null;
        this.ttsBusy = false;
        this.startSilenceTimer();
        this.drainSpeechQueue();
      });
    } catch {
      // TTS failed — text-only fallback
      this.ttsBusy = false;
      this.startSilenceTimer();
      this.drainSpeechQueue();
    }
  }

  /** Pull the next queued speech clip, if any, and play it. */
  private drainSpeechQueue(): void {
    const next = this.speechQueue.shift();
    if (next) void this.speakResponse(next.text, next.messageId);
  }

  /** Stop current playback and discard any queued speech. */
  private clearSpeechQueue(): void {
    this.speechQueue.length = 0;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.isSpeaking = false;
    this.ttsBusy = false;
  }

  private toggleTTS(): void {
    this.ttsEnabled = !this.ttsEnabled;
    if (this.speakerBtnEl) {
      this.speakerBtnEl.classList.toggle('da-fpd-speaker-off', !this.ttsEnabled);
      this.speakerBtnEl.setAttribute('aria-label', this.ttsEnabled ? 'Mute voice' : 'Unmute voice');
    }
    if (!this.ttsEnabled) {
      this.clearSpeechQueue();
    }
  }

  // ─── Messaging ──────────────────────────────────────────────────────────

  private sendVisitorMessage(text: string): void {
    if (!text) return;
    this.clearSilenceTimer();
    // Real visitor input — cancel any pending auto-continue tick and reset
    // the budget so the next showcase arc starts fresh.
    this.clearAutoContinueTimer();
    this.autoContinueRemaining = FullPageDemoMode.AUTO_CONTINUE_BUDGET;
    this.addUserMessage(text);
    this.typingEl?.classList.remove('da-fpd-hidden');
    this.config.sendVisitorMessage(text);
  }

  private startSilenceTimer(): void {
    this.clearSilenceTimer();
    if (!this.lastTurnEndedWithQuestion) return;
    if (this.silenceNudgeCount >= FullPageDemoMode.MAX_SILENCE_NUDGES) return;
    // If an auto-continue tick is in flight, the demo isn't really silent —
    // skip the nudge and let the tick land first.
    if (this.autoContinueTimer !== null) return;

    const delay = this.lastTurnWasAction
      ? FullPageDemoMode.SILENCE_NUDGE_MS_ACTION
      : FullPageDemoMode.SILENCE_NUDGE_MS_NORMAL;

    this.silenceTimer = setTimeout(() => {
      this.silenceTimer = null;
      this.silenceNudgeCount += 1;
      const variants = FullPageDemoMode.SILENCE_NUDGE_VARIANTS;
      const text = variants[Math.floor(Math.random() * variants.length)];
      this.addAgentMessage(text);
      this.speakResponse(text);
    }, delay);
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  /**
   * Schedule a synthetic continuation tick for the next agent turn when the
   * server marks this turn as auto-continue. Polls until the iframe command
   * queue drains and TTS playback finishes, then waits AUTO_CONTINUE_GRACE_MS
   * so a watching visitor has a window to interrupt before the tick fires.
   * Cancelled by any genuine visitor input (see {@link sendVisitorMessage}).
   */
  private scheduleAutoContinue(hint?: 'auto_continue' | 'wait_for_visitor'): void {
    this.clearAutoContinueTimer();
    if (hint !== 'auto_continue') return;
    if (this.autoContinueRemaining <= 0) return;

    const tick = () => {
      // Re-check guards inside the timer in case state changed.
      if (this.autoContinueRemaining <= 0) {
        this.autoContinueTimer = null;
        return;
      }
      // Wait for iframe commands to finish executing — auto-continuing
      // before they land would feed the agent stale verification data.
      if (this.processingCommands || this.commandQueue.length > 0) {
        this.autoContinueTimer = setTimeout(tick, FullPageDemoMode.AUTO_CONTINUE_POLL_MS);
        return;
      }
      // Wait for spoken narration so the next turn doesn't talk over the
      // current one. The TTS queue serializes, but starting the next LLM
      // call mid-utterance still steps on the cinematic pacing.
      if (this.isSpeaking || this.speechQueue.length > 0) {
        this.autoContinueTimer = setTimeout(tick, FullPageDemoMode.AUTO_CONTINUE_POLL_MS);
        return;
      }
      // All quiet — give the visitor a grace window to interrupt, then fire.
      this.autoContinueTimer = setTimeout(() => {
        this.autoContinueTimer = null;
        if (this.autoContinueRemaining <= 0) return;
        this.autoContinueRemaining -= 1;
        this.typingEl?.classList.remove('da-fpd-hidden');
        this.config.sendVisitorMessage('', { isAutoContinue: true });
      }, FullPageDemoMode.AUTO_CONTINUE_GRACE_MS);
    };

    // Initial poll — give iframe commands a beat to start before checking.
    this.autoContinueTimer = setTimeout(tick, FullPageDemoMode.AUTO_CONTINUE_POLL_MS);
  }

  private clearAutoContinueTimer(): void {
    if (this.autoContinueTimer) {
      clearTimeout(this.autoContinueTimer);
      this.autoContinueTimer = null;
    }
  }

  private handleSend(): void {
    const text = this.inputEl?.value.trim();
    if (!text) return;
    this.inputEl!.value = '';
    this.sendVisitorMessage(text);
  }

  private showLeadForm(fields: string[]): void {
    if (!this.messagesEl) return;

    const form = document.createElement('div');
    form.className = 'da-fpd-lead-form';

    let html = '<div class="da-fpd-lead-title">Interested? Leave your details:</div>';
    for (const field of fields) {
      const label = field.charAt(0).toUpperCase() + field.slice(1);
      html += `<input class="da-fpd-lead-input" type="${field === 'email' ? 'email' : 'text'}" placeholder="${label}" data-field="${field}" />`;
    }
    html += '<button class="da-fpd-lead-submit">Submit</button>';
    form.innerHTML = html;

    form.querySelector('.da-fpd-lead-submit')!.addEventListener('click', () => {
      const data: Record<string, string> = {};
      form.querySelectorAll<HTMLInputElement>('.da-fpd-lead-input').forEach((input) => {
        data[input.dataset.field!] = input.value;
      });
      this.config.sendLeadCapture(data);
      form.innerHTML = '<div class="da-fpd-lead-title">Thank you!</div>';
    });

    this.messagesEl.appendChild(form);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  /**
   * Render a customer success card inline in the chat panel. Triggered by
   * the cite_customer_story tool — the agent uses this to back a pain
   * claim with concrete proof (a real customer's outcome). Server-side
   * dispatch resolves the story id against the PDP and forwards the
   * pre-loaded fields, so this method just renders.
   */
  private showCustomerCard(params: Record<string, unknown>): void {
    if (!this.messagesEl) return;
    const title = typeof params.title === 'string' ? params.title : '';
    const outcome = typeof params.outcome === 'string' ? params.outcome : '';
    const quote = typeof params.quote === 'string' ? params.quote : '';
    const company = typeof params.company === 'string' ? params.company : '';
    const logoUrl = typeof params.logoUrl === 'string' ? params.logoUrl : '';

    const card = document.createElement('div');
    card.className = 'da-fpd-customer-card';
    const escape = (s: string): string =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    let html = '<div class="da-fpd-customer-card-header">';
    if (logoUrl) {
      html += `<img class="da-fpd-customer-card-logo" src="${escape(logoUrl)}" alt="${escape(company || 'logo')}" />`;
    }
    html += '<div class="da-fpd-customer-card-meta">';
    if (company) html += `<div class="da-fpd-customer-card-company">${escape(company)}</div>`;
    if (title) html += `<div class="da-fpd-customer-card-title">${escape(title)}</div>`;
    html += '</div></div>';
    if (outcome) html += `<div class="da-fpd-customer-card-outcome">${escape(outcome)}</div>`;
    if (quote) html += `<div class="da-fpd-customer-card-quote">&ldquo;${escape(quote)}&rdquo;</div>`;
    card.innerHTML = html;

    this.messagesEl.appendChild(card);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  /**
   * Render a booking CTA inline in the chat panel. Triggered by the
   * book_meeting tool. Opens the booking URL in a new tab on click —
   * embedding inline would require Cal.com / Calendly each to allow
   * iframing from any origin (they don't, by default). New-tab keeps
   * the demo session intact while the visitor books.
   */
  private showBookMeeting(params: Record<string, unknown>): void {
    if (!this.messagesEl) return;
    const url = typeof params.url === 'string' ? params.url : '';
    const ctaLabel = typeof params.ctaLabel === 'string' && params.ctaLabel
      ? params.ctaLabel
      : 'Book a 15-min walkthrough';
    if (!url) return;
    const escape = (s: string): string =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const wrap = document.createElement('div');
    wrap.className = 'da-fpd-book-meeting';
    wrap.innerHTML = `
      <div class="da-fpd-book-meeting-title">Pick a time that works:</div>
      <a class="da-fpd-book-meeting-cta" href="${escape(url)}" target="_blank" rel="noopener noreferrer">${escape(ctaLabel)}</a>
    `;

    this.messagesEl.appendChild(wrap);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // ─── UI Rendering ──────────────────────────────────────────────────────

  private renderLayout(): void {
    this.container = document.createElement('div');
    this.container.id = 'da-full-page-demo';
    // Open mode (not closed): rrweb's mutation observer can only walk the
    // subtree of an open shadow root, so closed mode hid every chat bubble,
    // typing indicator, tour panel, and input area from session replays.
    this.shadow = this.container.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = FULL_PAGE_DEMO_CSS;

    const layout = document.createElement('div');
    layout.className = 'da-fpd';
    layout.innerHTML = `
      <div class="da-fpd-iframe-wrap">
        <!--
          credentialless: load the iframe with anonymous browser storage so
          the demo session never inherits the visitor's existing cookies for
          the product. Without this, a visitor already logged into the real
          product would land in their own account when we navigate to the
          login URL — defeating the demo-account guarantee. Chromium-only
          today (~75% of users); Firefox/Safari fall back to whatever auth
          state the browser has, which the auto-login overrides for sites
          that bounce unauthenticated /login back to the form.

          We deliberately do NOT set the sandbox attribute. The combination
          we previously used (allow-same-origin + allow-scripts) is exactly
          the one Chromium warns about ("can escape its sandboxing") because
          a script inside the frame can remove the sandbox attribute via
          parent DOM access. The remaining sandbox restrictions (modals,
          downloads, top-navigation-without-activation) weren't pulling
          weight for a trusted customer demo URL — every PDP is authored by
          the product owner and we already control the URL we point at.
          Storage/cookie isolation comes from credentialless on Chromium and
          isn't something sandbox would have provided anyway.
        -->
        <iframe class="da-fpd-iframe" credentialless allow="clipboard-read; clipboard-write"></iframe>
        <div class="da-fpd-iframe-lock" aria-hidden="true">
          <div class="da-fpd-iframe-lock-badge">
            <span class="da-fpd-iframe-lock-dot"></span>
            <span>AI is driving the demo</span>
          </div>
        </div>
      </div>
      <div class="da-fpd-panel">
        <div class="da-fpd-header">
          <div class="da-fpd-header-left">
            <a class="da-fpd-brand" href="https://iraa.ai" target="_blank" rel="noopener" title="Powered by Iraa AI">
              <img class="da-fpd-brand-icon" src="${IRAA_LOGO_DATA_URI}" alt="" width="18" height="18" />
              <span class="da-fpd-brand-word">Iraa AI</span>
            </a>
            ${this.config.agentName === 'Iraa AI' ? '' : `
              <span class="da-fpd-brand-divider" aria-hidden="true"></span>
              <div class="da-fpd-avatar">${(this.config.agentName[0] || 'A').toUpperCase()}</div>
              <div class="da-fpd-name">${this.config.agentName}</div>
            `}
          </div>
          <div class="da-fpd-header-right">
            <button class="da-fpd-speaker" aria-label="Mute voice" style="display:none" title="Toggle voice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
            </button>
            <button class="da-fpd-exit" aria-label="Exit demo" title="Exit demo">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
              Exit Demo
            </button>
          </div>
        </div>
        <div class="da-fpd-tour da-fpd-hidden">
          <div class="da-fpd-tour-header">
            <span class="da-fpd-tour-label">Tour progress</span>
            <span class="da-fpd-tour-count">0</span>
          </div>
          <div class="da-fpd-tour-list"></div>
        </div>
        <div class="da-fpd-messages"></div>
        <div class="da-fpd-typing da-fpd-hidden">
          <span class="da-fpd-dot"></span><span class="da-fpd-dot"></span><span class="da-fpd-dot"></span>
        </div>
        <div class="da-fpd-input-area">
          <button class="da-fpd-mic" aria-label="Voice input" style="display:none" title="Voice input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          </button>
          <textarea class="da-fpd-input" placeholder="Type a message..." rows="1"></textarea>
          <button class="da-fpd-send" aria-label="Send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
        <div class="da-fpd-footer">
          <span class="da-fpd-footer-dot" aria-hidden="true"></span>
          <span>Powered by</span>
          <a class="da-fpd-footer-link" href="https://iraa.ai" target="_blank" rel="noopener">Iraa AI</a>
        </div>
      </div>
    `;

    this.shadow.appendChild(style);
    this.shadow.appendChild(layout);
    document.body.appendChild(this.container);

    // Cache refs
    this.iframe = layout.querySelector('.da-fpd-iframe')!;
    this.messagesEl = layout.querySelector('.da-fpd-messages')!;
    this.typingEl = layout.querySelector('.da-fpd-typing')!;
    this.inputEl = layout.querySelector('.da-fpd-input')!;
    this.micBtnEl = layout.querySelector('.da-fpd-mic')!;
    this.speakerBtnEl = layout.querySelector('.da-fpd-speaker')!;
    this.tourEl = layout.querySelector('.da-fpd-tour')!;
    this.tourListEl = layout.querySelector('.da-fpd-tour-list')!;

    // Events
    layout.querySelector('.da-fpd-send')!.addEventListener('click', () => this.handleSend());
    this.inputEl!.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
        return;
      }
      // Visitor is engaging with the input — cancel any pending auto-continue
      // tick so we don't talk over them between keystrokes. The actual budget
      // resets when they hit Enter (via sendVisitorMessage).
      if (this.autoContinueTimer !== null) {
        this.clearAutoContinueTimer();
      }
    });
    layout.querySelector('.da-fpd-exit')!.addEventListener('click', () => {
      this.stop();
      this.onExit?.();
    });
    this.micBtnEl!.addEventListener('click', () => { void this.toggleListening(); });
    this.speakerBtnEl!.addEventListener('click', () => this.toggleTTS());

    // Fade in
    requestAnimationFrame(() => {
      layout.classList.add('da-fpd-visible');
    });
  }

  private addAgentMessage(text: string): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = 'da-fpd-msg da-fpd-msg-agent';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // ─── Streaming message API (used by SSE consumer) ──────────────────────

  /**
   * Open an empty assistant message bubble and return a handle to it. Text
   * deltas appended via {@link appendStreamingDelta} grow this bubble in
   * place; {@link endStreamingAssistantMessage} finalizes it (TTS, etc.).
   */
  beginStreamingAssistantMessage(): { el: HTMLDivElement } {
    this.typingEl?.classList.add('da-fpd-hidden');
    const el = document.createElement('div');
    el.className = 'da-fpd-msg da-fpd-msg-agent';
    el.textContent = '';
    this.messagesEl?.appendChild(el);
    if (this.messagesEl) this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    return { el };
  }

  appendStreamingDelta(handle: { el: HTMLDivElement }, delta: string): void {
    if (!handle.el) return;
    handle.el.textContent = (handle.el.textContent ?? '') + delta;
    if (this.messagesEl) this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  /**
   * Finalize a streaming bubble. Triggers TTS on the full text (TTS doesn't
   * stream per token — speaking partial phrases sounds robotic — so we wait
   * for the complete reply before speaking). When `nextTurnHint` is
   * 'auto_continue' and the auto-continue budget allows, schedules a
   * synthetic continuation tick after the iframe commands and TTS for
   * this turn settle (see {@link scheduleAutoContinue}).
   */
  endStreamingAssistantMessage(
    handle: { el: HTMLDivElement },
    fullText: string,
    _agentId: string,
    nextTurnHint?: 'auto_continue' | 'wait_for_visitor',
    messageId?: string,
  ): void {
    if (handle.el && fullText && handle.el.textContent !== fullText) {
      // Edge case: a delta got lost mid-stream — overwrite with the
      // server's authoritative final text.
      handle.el.textContent = fullText;
    }
    // Mirror the WS-path bookkeeping at handleWSMessage('agent_response') so
    // the silence-nudge timer reads the right flags. Streaming doesn't
    // pre-set these because the widget doesn't see all iframe commands
    // upfront — they arrive on the SSE stream and queue as they land.
    this.lastTurnEndedWithQuestion = /\?\s*$/.test(fullText.trim());
    this.lastTurnWasAction = this.processingCommands || this.commandQueue.length > 0;

    if (fullText) {
      this.speakResponse(fullText, messageId);
    }
    this.scheduleAutoContinue(nextTurnHint);
  }

  private recordTourStop(url: string | undefined): void {
    if (!url || !this.tourEl || !this.tourListEl) return;
    const label = labelFromUrl(url);
    if (this.tourStops.some((s) => s.url === url)) return;
    this.tourStops.push({ url, label });

    if (this.tourStops.length === 1) {
      this.tourEl.classList.remove('da-fpd-hidden');
    }

    const items = this.tourStops
      .map((s, i) => {
        const isCurrent = i === this.tourStops.length - 1;
        const cls = isCurrent ? 'da-fpd-tour-item da-fpd-tour-current' : 'da-fpd-tour-item da-fpd-tour-done';
        const icon = isCurrent ? '●' : '✓';
        return `<div class="${cls}"><span class="da-fpd-tour-icon">${icon}</span><span class="da-fpd-tour-text">${escapeHtml(s.label)}</span></div>`;
      })
      .join('');
    this.tourListEl.innerHTML = items;

    const countEl = this.tourEl.querySelector('.da-fpd-tour-count');
    if (countEl) countEl.textContent = String(this.tourStops.length);
  }

  private addUserMessage(text: string): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = 'da-fpd-msg da-fpd-msg-user';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private addSystemMessage(text: string): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = 'da-fpd-msg da-fpd-msg-system';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // ─── Helpers ────────────────────────────────────────────────────────────

  private delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }
}

function pathFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    return (u.pathname.replace(/\/+$/, '') || '/');
  } catch {
    return null;
  }
}

function labelFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '') || '/';
    if (path === '/') return u.hostname;
    const parts = path.split('/').filter(Boolean);
    const last = parts[parts.length - 1] ?? '';
    return last
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()) || path;
  } catch {
    return url;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

// ─── CSS ──────────────────────────────────────────────────────────────────

const FULL_PAGE_DEMO_CSS = `
  :host { all: initial; }

  .da-fpd {
    /* ── Iraa brand tokens ───────────────────────────────────────────
       Marketing-site canonical purple + violet→indigo gradient. Slate
       deep-dark surfaces. Used by every rule below — change here to
       retheme the entire panel. */
    --iraa-primary: #7c3bec;
    --iraa-primary-strong: #6d28d9;
    --iraa-violet: #8b5cf6;
    --iraa-indigo: #6366f1;
    --iraa-violet-soft: #a78bfa;
    --iraa-gradient: linear-gradient(135deg, #6366f1, #8b5cf6);

    --iraa-bg-base: #0b1020;
    --iraa-bg-panel: #131a2e;
    --iraa-bg-elevated: #1a2238;
    --iraa-bg-card: #0f1426;
    --iraa-border: rgba(255, 255, 255, 0.07);
    --iraa-border-strong: rgba(255, 255, 255, 0.12);

    --iraa-text: #e7e9f3;
    --iraa-text-strong: #f5f6fb;
    --iraa-text-dim: #93a0b8;
    --iraa-text-muted: #64748b;

    --iraa-focus-ring: 0 0 0 3px rgba(124, 59, 236, 0.22);
    --iraa-glow-send: 0 8px 22px rgba(99, 102, 241, 0.32);
    --iraa-glow-bubble: 0 4px 14px rgba(124, 59, 236, 0.18);

    position: fixed;
    inset: 0;
    display: flex;
    z-index: 2147483647;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    color: var(--iraa-text);
    background: var(--iraa-bg-base);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .da-fpd.da-fpd-visible {
    opacity: 1;
  }

  /* ── Iframe ── */

  .da-fpd-iframe-wrap {
    flex: 1;
    min-width: 0;
    position: relative;
    background: #fff;
    border-radius: 12px;
    margin: 12px;
    overflow: hidden;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.06);
  }

  .da-fpd-iframe {
    width: 100%;
    height: 100%;
    border: none;
    pointer-events: none;
  }

  /* Transparent overlay above the iframe — blocks visitor clicks/scrolls so
     they can't interfere while the agent is driving. The agent's iframe
     commands still flow via postMessage, which doesn't go through the DOM. */
  .da-fpd-iframe-lock {
    position: absolute;
    inset: 0;
    background: transparent;
    cursor: not-allowed;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 16px;
    pointer-events: auto;
  }

  .da-fpd-iframe-lock-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(11, 16, 32, 0.85);
    color: var(--iraa-text);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.01em;
    border: 1px solid rgba(124, 59, 236, 0.4);
    backdrop-filter: blur(8px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    pointer-events: none;
  }

  .da-fpd-iframe-lock-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--iraa-violet-soft);
    box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
    animation: da-fpd-lock-pulse 1.6s infinite;
  }

  @keyframes da-fpd-lock-pulse {
    0% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7); }
    70% { box-shadow: 0 0 0 8px rgba(167, 139, 250, 0); }
    100% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0); }
  }

  /* ── Panel ── */

  .da-fpd-panel {
    width: 380px;
    min-width: 320px;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, var(--iraa-bg-panel) 0%, #0f1528 100%);
    border-left: 1px solid var(--iraa-border);
    overflow: hidden;
  }

  .da-fpd-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--iraa-border);
    background: var(--iraa-bg-base);
  }

  /* Thin gradient seal under the header — visually marks "Iraa-powered chrome
     ends, agent's chat begins". 1px high, no layout impact. */
  .da-fpd-header::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: var(--iraa-gradient);
    opacity: 0.5;
    pointer-events: none;
  }

  .da-fpd-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .da-fpd-brand {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    color: var(--iraa-text-strong);
    opacity: 0.85;
    transition: opacity 0.15s;
  }

  .da-fpd-brand:hover { opacity: 1; }

  .da-fpd-brand-icon {
    flex-shrink: 0;
    object-fit: contain;
    border-radius: 4px;
  }

  .da-fpd-brand-word {
    font-weight: 600;
    font-size: 13px;
    letter-spacing: -0.01em;
    color: var(--iraa-text-strong);
  }

  .da-fpd-brand-divider {
    width: 1px;
    height: 18px;
    background: var(--iraa-border-strong);
    opacity: 0.6;
    flex-shrink: 0;
  }

  .da-fpd-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .da-fpd-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--iraa-gradient);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(124, 59, 236, 0.3);
  }

  .da-fpd-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--iraa-text-strong);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .da-fpd-exit {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid var(--iraa-border-strong);
    border-radius: 8px;
    background: transparent;
    color: var(--iraa-text-dim);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .da-fpd-exit:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--iraa-text-strong);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .da-fpd-speaker, .da-fpd-mic {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid var(--iraa-border-strong);
    background: transparent;
    color: var(--iraa-text-dim);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
  }

  .da-fpd-speaker:hover, .da-fpd-mic:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--iraa-text-strong);
  }

  .da-fpd-speaker-off {
    color: #475569 !important;
    opacity: 0.5;
  }

  .da-fpd-mic-active {
    background: #dc2626 !important;
    border-color: #dc2626 !important;
    color: #fff !important;
    animation: da-fpd-pulse 1.5s infinite;
  }

  @keyframes da-fpd-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.4); }
    50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); }
  }

  /* ── Tour ── */

  .da-fpd-tour {
    padding: 10px 14px;
    border-bottom: 1px solid var(--iraa-border);
    background: rgba(255, 255, 255, 0.02);
  }

  .da-fpd-tour-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
  }

  .da-fpd-tour-count {
    font-variant-numeric: tabular-nums;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 600;
  }

  .da-fpd-tour-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 120px;
    overflow-y: auto;
  }

  .da-fpd-tour-list::-webkit-scrollbar { width: 3px; }
  .da-fpd-tour-list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.14); border-radius: 2px;
  }

  .da-fpd-tour-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    padding: 4px 6px;
    border-radius: 6px;
  }

  .da-fpd-tour-icon {
    width: 14px;
    text-align: center;
    font-size: 10px;
  }

  .da-fpd-tour-text {
    color: rgba(255, 255, 255, 0.75);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .da-fpd-tour-done .da-fpd-tour-icon { color: #22c55e; }
  .da-fpd-tour-current {
    background: rgba(124, 59, 236, 0.14);
  }
  .da-fpd-tour-current .da-fpd-tour-icon { color: var(--iraa-violet-soft); }
  .da-fpd-tour-current .da-fpd-tour-text { color: var(--iraa-text); font-weight: 500; }

  /* ── Messages ── */

  .da-fpd-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .da-fpd-messages::-webkit-scrollbar {
    width: 4px;
  }

  .da-fpd-messages::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.14);
    border-radius: 2px;
  }

  .da-fpd-msg {
    padding: 10px 14px;
    border-radius: 12px;
    max-width: 90%;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 13px;
  }

  .da-fpd-msg-agent {
    background: var(--iraa-bg-elevated);
    border: 1px solid var(--iraa-border);
    color: var(--iraa-text);
    align-self: flex-start;
    border-bottom-left-radius: 4px;
  }

  .da-fpd-msg-user {
    background: var(--iraa-gradient);
    color: #fff;
    align-self: flex-end;
    border-bottom-right-radius: 4px;
    box-shadow: var(--iraa-glow-bubble);
  }

  .da-fpd-msg-system {
    background: rgba(255, 255, 255, 0.05);
    color: var(--iraa-text-dim);
    align-self: center;
    font-size: 12px;
    text-align: center;
  }

  /* ── Typing ── */

  .da-fpd-typing {
    display: flex;
    gap: 4px;
    padding: 0 16px 8px;
    align-items: center;
  }

  .da-fpd-hidden {
    display: none !important;
  }

  .da-fpd-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #64748b;
    animation: da-fpd-bounce 1.4s infinite;
  }

  .da-fpd-dot:nth-child(2) { animation-delay: 0.2s; }
  .da-fpd-dot:nth-child(3) { animation-delay: 0.4s; }

  @keyframes da-fpd-bounce {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-4px); }
  }

  /* ── Input ── */

  .da-fpd-input-area {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid var(--iraa-border);
    background: var(--iraa-bg-base);
  }

  .da-fpd-input {
    flex: 1;
    border: 1px solid var(--iraa-border-strong);
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 13px;
    font-family: inherit;
    resize: none;
    outline: none;
    background: var(--iraa-bg-elevated);
    color: var(--iraa-text);
    min-height: 20px;
    max-height: 80px;
    transition: border-color 0.15s, box-shadow 0.15s;
  }

  .da-fpd-input::placeholder {
    color: var(--iraa-text-muted);
  }

  .da-fpd-input:focus {
    border-color: var(--iraa-primary);
    box-shadow: var(--iraa-focus-ring);
  }

  .da-fpd-send {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: none;
    background: var(--iraa-gradient);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: filter 0.15s, transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.22);
  }

  .da-fpd-send:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: var(--iraa-glow-send);
  }

  .da-fpd-send:active {
    transform: translateY(0);
    filter: brightness(0.95);
  }

  /* ── Footer (Powered by Iraa) ── */

  .da-fpd-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 16px 10px;
    background: var(--iraa-bg-base);
    border-top: 1px solid var(--iraa-border);
    font-size: 11px;
    letter-spacing: 0.02em;
    color: var(--iraa-text-muted);
  }

  .da-fpd-footer-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--iraa-gradient);
    box-shadow: 0 0 6px rgba(124, 59, 236, 0.5);
    flex-shrink: 0;
  }

  .da-fpd-footer-link {
    color: var(--iraa-text-dim);
    text-decoration: none;
    font-weight: 600;
    background: var(--iraa-gradient);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    transition: filter 0.15s;
  }

  .da-fpd-footer-link:hover {
    filter: brightness(1.15);
  }

  /* ── Lead Form ── */

  .da-fpd-lead-form {
    background: var(--iraa-bg-elevated);
    border: 1px solid var(--iraa-border);
    border-radius: 12px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-self: flex-start;
    max-width: 90%;
  }

  .da-fpd-lead-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--iraa-text);
  }

  .da-fpd-lead-input {
    padding: 8px 10px;
    border: 1px solid var(--iraa-border-strong);
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    background: var(--iraa-bg-base);
    color: var(--iraa-text);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }

  .da-fpd-lead-input:focus {
    border-color: var(--iraa-primary);
    box-shadow: var(--iraa-focus-ring);
  }

  .da-fpd-lead-input::placeholder {
    color: var(--iraa-text-muted);
  }

  .da-fpd-lead-submit {
    padding: 8px 16px;
    background: var(--iraa-gradient);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.15s, transform 0.15s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.22);
  }

  .da-fpd-lead-submit:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  .da-fpd-lead-submit:active {
    transform: translateY(0);
    filter: brightness(0.95);
  }

  /* ── Customer success card (cite_customer_story) ── */

  .da-fpd-customer-card {
    margin: 8px 0;
    padding: 14px;
    background: #1a1a1a;
    border: 1px solid #303030;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .da-fpd-customer-card-header {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .da-fpd-customer-card-logo {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    object-fit: cover;
    background: #2a2a2a;
    flex-shrink: 0;
  }

  .da-fpd-customer-card-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .da-fpd-customer-card-company {
    font-size: 13px;
    font-weight: 600;
    color: #e5e5e5;
  }

  .da-fpd-customer-card-title {
    font-size: 12px;
    color: #a1a1a1;
  }

  .da-fpd-customer-card-outcome {
    font-size: 13px;
    color: #e5e5e5;
    line-height: 1.5;
  }

  .da-fpd-customer-card-quote {
    font-size: 12px;
    color: #a1a1a1;
    font-style: italic;
    border-left: 2px solid var(--iraa-primary);
    padding-left: 10px;
    line-height: 1.5;
  }

  /* ── Booking CTA (book_meeting) ── */

  .da-fpd-book-meeting {
    margin: 8px 0;
    padding: 14px;
    background: #1a1a1a;
    border: 1px solid #303030;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .da-fpd-book-meeting-title {
    font-size: 13px;
    color: #e5e5e5;
    font-weight: 500;
  }

  .da-fpd-book-meeting-cta {
    display: inline-block;
    padding: 10px 16px;
    background: var(--iraa-gradient);
    color: #fff;
    border-radius: 8px;
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    transition: filter 0.15s, transform 0.15s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.22);
  }

  .da-fpd-book-meeting-cta:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  /* ── Mobile ── */

  @media (max-width: 768px) {
    .da-fpd {
      flex-direction: column;
    }

    .da-fpd-iframe-wrap {
      flex: none;
      height: 55%;
      margin: 8px 8px 0;
      border-radius: 10px;
    }

    .da-fpd-panel {
      width: 100%;
      min-width: unset;
      flex: 1;
      border-left: none;
      border-top: 1px solid var(--iraa-border);
    }
  }
`;
